import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendContactMessage } from '../../services/compositionApi';
import './ContactForm.css';

// Formulaire de contact (page FAQ, 2026-10-29) : remplace l'adresse e-mail affichee. Le message part a l'equipe par e-mail
// (backend/routes/contact.js) ; la reponse arrive dans la boite de la personne. Un champ piege invisible (« website ») ecarte les
// robots. Erreurs dites champ par champ (aria-invalid + message relie), confirmation annoncee, focus sur le premier champ fautif.
const VIDE = { name: '', email: '', subject: '', order: '', message: '', website: '' };

export default function ContactForm() {
  const { t } = useTranslation('home');
  const [valeurs, setValeurs] = useState(VIDE);
  const [erreurs, setErreurs] = useState({});
  const [etat, setEtat] = useState('saisie'); // 'saisie' | 'envoi' | 'envoye'
  const [erreur, setErreur] = useState('');
  const formRef = useRef(null);

  const changer = (champ) => (event) => {
    setValeurs((v) => ({ ...v, [champ]: event.target.value }));
    if (erreurs[champ]) setErreurs((e) => ({ ...e, [champ]: null }));
  };

  const verifier = () => {
    const e = {};
    if (valeurs.name.trim().length < 2) e.name = 'name_required';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(valeurs.email.trim())) e.email = 'email_invalid';
    if (valeurs.message.trim().length < 10) e.message = 'message_too_short';
    return e;
  };

  const focaliserPremiere = (e) => {
    const premier = ['name', 'email', 'message'].find((c) => e[c]);
    if (premier) formRef.current?.querySelector(`[name="${premier}"]`)?.focus();
  };

  const envoyer = async (event) => {
    event.preventDefault();
    setErreur('');
    const e = verifier();
    setErreurs(e);
    if (Object.keys(e).length) { focaliserPremiere(e); return; }
    setEtat('envoi');
    try {
      await sendContactMessage(valeurs);
      setEtat('envoye');
      setValeurs(VIDE);
    } catch (err) {
      setEtat('saisie');
      if (err.fields) { setErreurs(err.fields); focaliserPremiere(err.fields); }
      setErreur(err.message || t('faqPage.contact.failed'));
    }
  };

  if (etat === 'envoye') {
    return (
      <div className="contact-form contact-sent" role="status">
        <p className="contact-sent-title">{t('faqPage.contact.sentTitle')}</p>
        <p>{t('faqPage.contact.sentBody')}</p>
        <button type="button" className="btn btn-outline" onClick={() => setEtat('saisie')}>{t('faqPage.contact.another')}</button>
      </div>
    );
  }

  const champ = (nom, { type = 'text', obligatoire = false, autoComplete, multiline = false, max } = {}) => {
    const id = `contact-${nom}`;
    const code = erreurs[nom];
    const props = {
      id, name: nom, value: valeurs[nom], onChange: changer(nom), maxLength: max, autoComplete,
      className: `input-luxe${code ? ' input-error' : ''}`,
      'aria-invalid': code ? 'true' : undefined,
      'aria-describedby': code ? `${id}-err` : undefined,
      required: obligatoire || undefined,
      disabled: etat === 'envoi'
    };
    return (
      <label className="contact-field" htmlFor={id}>
        <span>{t(`faqPage.contact.fields.${nom}`)}{obligatoire ? <i aria-hidden="true"> *</i> : <em> {t('faqPage.contact.optional')}</em>}</span>
        {multiline ? <textarea rows={6} {...props} /> : <input type={type} {...props} />}
        {code && <small id={`${id}-err`} className="contact-err">{t(`faqPage.contact.errors.${code}`)}</small>}
      </label>
    );
  };

  return (
    <form ref={formRef} className="contact-form" onSubmit={envoyer} noValidate>
      <div className="contact-row">
        {champ('name', { obligatoire: true, autoComplete: 'name', max: 80 })}
        {champ('email', { type: 'email', obligatoire: true, autoComplete: 'email', max: 200 })}
      </div>
      <div className="contact-row">
        {champ('subject', { max: 120 })}
        {champ('order', { max: 60 })}
      </div>
      {champ('message', { obligatoire: true, multiline: true, max: 5000 })}
      {/* Piege a robots : invisible et hors du parcours clavier, jamais rempli par une personne. */}
      <div className="contact-trap" aria-hidden="true">
        <label htmlFor="contact-website">Website</label>
        <input id="contact-website" name="website" tabIndex={-1} autoComplete="off" value={valeurs.website} onChange={changer('website')} />
      </div>
      {erreur && <p className="contact-err contact-err-global" role="alert">{erreur}</p>}
      <p className="contact-note">{t('faqPage.contact.privacy')}</p>
      <button type="submit" className="btn btn-primary" disabled={etat === 'envoi'}>
        {etat === 'envoi' ? t('faqPage.contact.sending') : t('faqPage.contact.send')}
      </button>
    </form>
  );
}
